import React from 'react';
import { Grid } from '@material-ui/core';
import styled from 'styled-components/macro';
import { Input, Button } from '@deeptrue-frontend/ui';
import { Formik, Field as FormikField, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import ErrorMessages from 'components/Fields/ErrorMessages';
import { useFormatMessage } from 'components/IntlProvider';
import { FormatMessage } from 'components/IntlProvider/hooks/hooks';

export interface FormValues {
  current_password: string;
  new_password: string;
  new_password_confirm: string;
  non_field_errors?: any;
}

export type SubmitHandler = (
  values: FormValues,
  helpers: FormikHelpers<FormValues>
) => void;

const StyledForm = styled.form`
  margin-top: 1rem;
  width: 100%;
`;

const getValidationSchema = (t: FormatMessage) =>
  Yup.object().shape<FormValues>({
    current_password: Yup.string().required(
      t('errors.fields.required', 'Field is required')
    ),
    new_password: Yup.string().required(
      t('errors.fields.required', 'Field is required')
    ),
    new_password_confirm: Yup.string()
      .required(t('errors.fields.required', 'Field is required'))
      .oneOf(
        [Yup.ref('new_password'), null],
        t('errors.fields.password.match', 'Passwords do not match')
      ),
  });

interface Props {
  initialValues: FormValues;
  onSubmit: SubmitHandler;
}

const Password = ({ onSubmit, initialValues }: Props) => {
  const t = useFormatMessage();

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={getValidationSchema(t)}
    >
      {({ handleSubmit, isSubmitting, errors, touched }) => {
        const nonFieldErrors = errors.non_field_errors;
        return (
          <Grid item xs={12} md={7}>
            <StyledForm onSubmit={handleSubmit}>
              <FormikField
                as={Input}
                label={t('forms.password.current', 'Current password')}
                name="current_password"
                hasError={touched.current_password && errors.current_password}
                helperText={touched.current_password && errors.current_password}
                type="password"
              />

              <FormikField
                as={Input}
                label={t('forms.password.new', 'New password')}
                name="new_password"
                hasError={touched.new_password && errors.new_password}
                helperText={touched.new_password && errors.new_password}
                type="password"
              />

              <FormikField
                as={Input}
                label={t(
                  'forms.password.confirmnewpassword',
                  'Confirm new password'
                )}
                name="new_password_confirm"
                hasError={
                  touched.new_password_confirm && errors.new_password_confirm
                }
                helperText={
                  touched.new_password_confirm && errors.new_password_confirm
                }
                type="password"
              />
              {nonFieldErrors && <ErrorMessages errors={nonFieldErrors} />}
              <Button
                mt="2rem"
                width="100%"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                type="submit"
              >
                {t('forms.password.submit', 'Change password')}
              </Button>
            </StyledForm>
          </Grid>
        );
      }}
    </Formik>
  );
};

export default Password;
